import {
  EditorText,
  EvergreenType,
  GroupPostAttributesSchedulingType,
  GroupPostAttributesType,
  MediaTypePostEditorState,
  PostEditorContextEnum,
  PostEditorLink,
  PostEditorRecurringIntervalType,
  PostGroupsType,
  ReadonlyGroupPostAttributesType,
  VariationMeta
} from '../../../models/post-editor';
import {
  BitlyPreference,
  CategoryFieldsFragment,
  PostItemFragment,
  GetBitlyAccountsQuery,
  GetPostsEditorQuery,
  LinkDestination,
  PostMultiMediaAttachMode,
  PostRecurringInterval,
  PostStatus,
  PostTiming,
  ThirdPartyProfileSearchType,
  TimezoneSetting,
  UtmInput
} from '../../../models/graphql';
import { ImmutabilityContext } from '../states/effects/customImmutabilityContext';
import { SelectedSocialProfileValue } from '../../../components/socialProfiles/SocialProfileSelectorList/SocialProfileSelectorList.view';
import { MakeRequired, SortFunction } from '../../../models/basic-objects';
import { cloneDeep } from 'lodash';
import { MediaUploadStatus } from '../../../constants/media';
import { deserializeText } from '../TextEditor/helpers/serializer';
import { showToast } from '../../../helpers/showToast';
import { BaseMention, CustomParagraph } from '../../../models/slate-editor';
import { ScheduleHelpers } from '../Schedule/Schedule.helpers';
import { calculateVariations } from '../../../helpers/calculateVariations';
import { profilesHelpers } from '../../Settings/Profiles/Profiles.helpers';
import { getExistingUtmKeys, IsKeyOfUtmInput, UtmInputKeys } from '../states/reducers/link.reducer';
import { getUrlParams } from '../../../helpers/getUrlParams';
import { mapPostTikTokToTikTokProfileValues } from './mapSelectedProfilesValues';

// TODO we should add unit tests for all these helpers / transformers
const getExpiryType = (post: GetPostsEditorQuery['latestFamilyMembersOfPost'][number]): EvergreenType['expireType'] => {
  let result: EvergreenType['expireType'] = [];
  if (post.maxRepeats) result = ['count'];
  if (post.expiryLocalDate) result = [...result, 'date'];
  return result;
};

const getRecurringType = (
  recurring: GetPostsEditorQuery['latestFamilyMembersOfPost'][number]['recurringInterval']
): PostEditorRecurringIntervalType | null => {
  switch (recurring) {
    case PostRecurringInterval.DAY:
      return PostEditorRecurringIntervalType.days;
    case PostRecurringInterval.WEEK:
      return PostEditorRecurringIntervalType.weeks;
    case PostRecurringInterval.YEAR:
      return PostEditorRecurringIntervalType.years;
    default:
      return null;
  }
};

type TransformLinkInputUtm = {
  __typename?: 'UTM';
  campaign: string;
  content: string;
  medium: string;
  source: string;
  term: string;
};
const getLinkUtmInput = (input: TransformLinkInputUtm): UtmInput | null =>
  input
    ? {
        campaign: input.campaign,
        content: input.content,
        medium: input.medium,
        source: input.source,
        term: input.term
      }
    : null;

// if initial utm params are not present in the final we should remove true. It means we have removed them.
const getRemoveUtm = (
  link: MakeRequired<GetPostsEditorQuery['latestFamilyMembersOfPost'][number], 'link'>['link']
): boolean => {
  if (!link) return false;
  const actualExistingLink = link.initialUrlBitlink ? link.expandedUrl : link.initialUrl;
  const existingUtmKeys = getExistingUtmKeys(actualExistingLink);
  if (!existingUtmKeys.length) return false;
  const destinationUrl =
    link.bitlyPreference === BitlyPreference.DEFAULT && link.bitlyDestinationUrl
      ? link.bitlyDestinationUrl
      : link.url || '';
  const initialLinkUrl = getUrlParams(actualExistingLink);
  const finalLinkUrl = getUrlParams(destinationUrl);
  if (!initialLinkUrl || !finalLinkUrl) return false;
  const { searchParams: initialSearchParams } = initialLinkUrl;
  const { searchParams: finalSearchParams } = finalLinkUrl;
  let urlKeys: string[] = [];
  const utmKeys = UtmInputKeys.map(k => `utm_${k}`);
  for (const key of Array.from(initialSearchParams.keys())) {
    if (utmKeys.includes(key)) {
      urlKeys = [...urlKeys, key];
    }
  }
  if (!urlKeys.length) return false;
  return urlKeys.reduce((prev, current) => {
    if (prev) return true;
    const currentValue = initialSearchParams.get(current);
    if (!currentValue) return false;
    return currentValue !== finalSearchParams.get(current);
  }, false);
};

const getInitialUtm = (
  link: MakeRequired<GetPostsEditorQuery['latestFamilyMembersOfPost'][number], 'link'>['link']
): Partial<UtmInput> => {
  if (!link) return {};
  const actualExistingLink = link.destination === LinkDestination.EXPANDED ? link.expandedUrl : link.initialUrl;
  const existingUtmKeys = getExistingUtmKeys(actualExistingLink);
  if (!existingUtmKeys.length) return {};
  const destinationUrl =
    link.bitlyPreference === BitlyPreference.DEFAULT && link.bitlyDestinationUrl ? link.bitlyDestinationUrl : link.url;
  const initialLinkUrl = getUrlParams(actualExistingLink);
  const finalLinkUrl = getUrlParams(destinationUrl);
  if (!initialLinkUrl || !finalLinkUrl) return {};
  const { searchParams: initialSearchParams } = initialLinkUrl;
  // const { searchParams: finalSearchParams } = finalLinkUrl;
  let urlKeys: string[] = [];
  const utmKeys = UtmInputKeys.map(k => `utm_${k}`);
  for (const key of Array.from(initialSearchParams.keys())) {
    if (utmKeys.includes(key)) {
      urlKeys = [...urlKeys, key];
    }
  }
  if (!urlKeys.length) return {};
  return urlKeys.reduce((prev, current) => {
    const strippedCurrent = current.replace('utm_', '');
    if (!IsKeyOfUtmInput(strippedCurrent)) return prev;
    const currentValue = initialSearchParams.get(current);
    return {
      ...prev,
      ...(currentValue ? { [strippedCurrent]: currentValue } : {})
    };
  }, {});
};

const getAddUtm = (
  link: MakeRequired<GetPostsEditorQuery['latestFamilyMembersOfPost'][number], 'link'>['link']
): boolean => {
  const { utm } = link ?? {};
  if (!utm) return false;
  const initialUtm = getInitialUtm(link);
  return Object.entries(utm).reduce((prev, current) => {
    if (prev) return true;
    const [key, value] = current;
    if (key.includes('__')) return false; // this will make sure to omit __typename that can be part of UTM.
    return !!value && !initialUtm.hasOwnProperty(key);
  }, false);
};

const transformLink = (
  link: MakeRequired<GetPostsEditorQuery['latestFamilyMembersOfPost'][number], 'link'>['link']
): PostEditorLink | null => {
  return link
    ? {
        destination: link.destination,
        useBitly: link.bitlyPreference === BitlyPreference.DEFAULT,
        initialUrl: link.initialUrl,
        googleCallToAction: link.googleCallToAction,
        url: link.url,
        utmTracking: link.utm ? getLinkUtmInput(link.utm) : null,
        addUtm: getAddUtm(link),
        removeUtm: getRemoveUtm(link),
        removePreview: link.previewRemoved,
        linkMetadata: {
          __typename: 'LinkMetadata',
          error: null,
          initialUrl: link.initialUrl,
          expandedUrl: link.expandedUrl,
          domain: getUrlParams(link.expandedUrl)?.host || '', // we don't need domain so we can set it to ''
          images: [], // TODO we currently don't have this data in the DB so we can set it to [] sinc eit is not used
          isBitlink: link.initialUrlBitlink,
          openGraph: link.openGraph
            ? {
                __typename: 'OpenGraph',
                image: link.openGraph.image,
                title: link.openGraph.title,
                description: link.openGraph.description
              }
            : null,
          twitterCard: link.twitterCard
            ? {
                __typename: 'TwitterCard',
                image: link.twitterCard.image,
                title: link.twitterCard.title,
                description: link.twitterCard.description,
                card: link.twitterCard.card
              }
            : null
        }
      }
    : null;
};

const getQueuePosition = (
  post: GetPostsEditorQuery['latestFamilyMembersOfPost'][number],
  type: PostEditorContextEnum
): GroupPostAttributesSchedulingType['queuePosition'] => {
  if (post.timing !== PostTiming.QUEUE) return null;
  if (
    (post.status === PostStatus.DRAFT ||
      [PostEditorContextEnum.duplicate, PostEditorContextEnum.reschedule_new].includes(type)) &&
    post.draftQueuePosition
  )
    return post.draftQueuePosition;
  return post.status === PostStatus.ACTIVE ? 'CURRENT' : post.draftQueuePosition || null;
};

const getAllTexts = (
  post: GetPostsEditorQuery['latestFamilyMembersOfPost'][number],
  method: ThirdPartyProfileSearchType | null
): EditorText[] => {
  const { texts, link } = post;
  const mappedLink: PostEditorLink | null = link ? transformLink(link) : null;
  const baseMentions = post.mentions.map<BaseMention>(mention => ({ ...mention, method }));
  return texts.map<EditorText>((text, index) => {
    const result = deserializeText(text, baseMentions, mappedLink);
    return {
      id: index + 1,
      text: result
    };
  });
};

const mapMedia = (
  media: Array<
    | GetPostsEditorQuery['latestFamilyMembersOfPost'][number]['photos'][number]
    | GetPostsEditorQuery['latestFamilyMembersOfPost'][number]['videos'][number]
  >
): MediaTypePostEditorState[] =>
  media.map(item => ({
    id: item.id,
    url: item.url,
    type: item.type,
    gif: item.__typename === 'Photo' && item.gif,
    thumbnailUrl: item.thumbnailUrl,
    loading: false,
    status: MediaUploadStatus.COMPLETED,
    proportions: item.proportions,
    width: item.width,
    height: item.height,
    length: item.__typename === 'Video' && item.length ? item.length : 0,
    transcoded: item.__typename === 'Video' && item.transcoded,
    filesize: item.filesize,
    externalUpload: false
  }));

const getVariation = (
  varyMedia: GetPostsEditorQuery['latestFamilyMembersOfPost'][number]['variationMeta']
): PostMultiMediaAttachMode | null => {
  if (!varyMedia) return null;
  return varyMedia.multiMediaAttachMode;
};

const getVariationMeta = (
  post: GetPostsEditorQuery['latestFamilyMembersOfPost'][number],
  method: ThirdPartyProfileSearchType | null,
  context: PostEditorContextEnum
): VariationMeta => {
  // if we are duplicating we should reset the variations
  const createNewPost = [PostEditorContextEnum.duplicate].includes(context);
  const currentIndex = createNewPost ? 1 : post.variationMeta?.currentIndex;
  const prevPostedCount = createNewPost ? 0 : post.prevPostedCount ?? 0;
  const medias = mapMedia([...post.photos, ...post.videos]);
  if (currentIndex !== undefined && prevPostedCount !== undefined) {
    const allTexts = getAllTexts(post, method);
    const { mediaVariationIndex, textVariationIndex, total } = calculateVariations(
      allTexts,
      medias,
      post.variationMeta?.multiMediaAttachMode || null,
      currentIndex
    );
    return {
      currentVariationIndex: currentIndex,
      total,
      prevPostedCount,
      mediaVariationIndex,
      textVariationIndex
    };
  }

  return {
    currentVariationIndex: 1,
    total: 0,
    prevPostedCount: 0,
    mediaVariationIndex: null,
    textVariationIndex: null
  };
};

export const getThirdPartyProfileSearchType = (
  type: CategoryFieldsFragment['defaultSocialProfiles'][number]['__typename']
): ThirdPartyProfileSearchType | null => {
  switch (type) {
    case 'FacebookSocialProfile':
      return ThirdPartyProfileSearchType.FACEBOOK_PAGE;
    case 'TwitterSocialProfile':
      return ThirdPartyProfileSearchType.TWITTER_USER;
    case 'LinkedInSocialProfile':
      return ThirdPartyProfileSearchType.LINKEDIN_COMPANY;
    case 'InstagramSocialProfile':
      return ThirdPartyProfileSearchType.INSTAGRAM_BUSINESS_PROFILE;
    default:
      return null;
  }
};

const getParentId = (context: PostEditorContextEnum, post: PostItemFragment): string | null => {
  // duplicate is the only context we call with this function that needs to return null
  if (context === PostEditorContextEnum.duplicate) return null;
  return post.status === PostStatus.PUBLISHED && post.parentId ? post.parentId : null;
};

const getPostId = (context: PostEditorContextEnum, post: PostItemFragment): string | null => {
  // if we are editing a post we keep the post id. in all other cases we should create new, so no post id should be set
  switch (context) {
    case PostEditorContextEnum.reschedule_edit:
    case PostEditorContextEnum.restore_trashed:
      return post.id;
    case PostEditorContextEnum.edit:
      return post.status === PostStatus.PUBLISHED ? null : post.id;
    default:
      return null;
  }
};

export const mapPostToGroupPostAttributesType = (
  post: PostItemFragment,
  state: GroupPostAttributesType,
  context: PostEditorContextEnum
): GroupPostAttributesType => {
  try {
    const { update: updateImmutable } = ImmutabilityContext;
    const method: ThirdPartyProfileSearchType | null = getThirdPartyProfileSearchType(post.socialProfile?.__typename);
    // this switch block is used for mentions. we need a method in order to get baseMentions
    const allTexts = getAllTexts(post, method);
    return updateImmutable<GroupPostAttributesType>(state, {
      scheduling: {
        evergreen: {
          active: {
            $apply: current => (post.isEvergreen && !post.recurringInterval && !post.recurringValue) ?? current
          },
          expiryMaxRepeats: { $apply: current => post.maxRepeats ?? current },
          expiryDate: { $apply: current => post.expiryLocalDate ?? current },
          expireType: { $set: getExpiryType(post) }
        },
        recurring: {
          active: {
            $apply: current => (post.recurringInterval && typeof post.recurringValue === 'number') ?? current
          },
          expiryMaxRepeats: {
            $apply: current => (typeof post.recurringValue === 'number' ? post.recurringValue : current)
          },
          expiryDate: { $apply: current => post.expiryLocalDate ?? current },
          expireType: { $set: getExpiryType(post) },
          date: {
            $apply: current =>
              post.recurringInterval &&
              typeof post.recurringValue === 'number' &&
              post.futureScheduledLocalDateTimes.length
                ? post.futureScheduledLocalDateTimes[0]
                : current
          }
        },
        postTiming: { $set: post.timing },
        queuePosition: {
          $set: getQueuePosition(post, context)
        },
        recurringType: { $set: getRecurringType(post.recurringInterval) },
        recurringValue: { $apply: current => post.recurringValue ?? current },
        scheduledLocalDateTimes: {
          $set: [
            ...post.publishedUtcDateTimes
              .map(dateTime => ({
                dateTime: ScheduleHelpers.convertUtcToZonedFormattedDate(
                  dateTime,
                  post.scheduledTimezone || state.scheduling.scheduledTimezone || TimezoneSetting.UTC
                ),
                isPast: true
              }))
              .sort(ScheduleHelpers.sortScheduledDateTimes),
            ...post.futureScheduledLocalDateTimes
              .map(dateTime => ({
                dateTime,
                isPast: ScheduleHelpers.checkIfScheduledPostIsPast(
                  dateTime,
                  post.scheduledTimezone || state.scheduling.scheduledTimezone || TimezoneSetting.UTC
                )
              }))
              .sort(ScheduleHelpers.sortScheduledDateTimes)
          ]
        },
        scheduledTimezone: { $apply: current => post.scheduledTimezone ?? current }
      },
      content: {
        link: { $apply: current => (post.link ? transformLink(post.link) : current) },
        media: {
          $apply: (current: MediaTypePostEditorState[]) =>
            post.photos.length || post.videos.length ? mapMedia([...post.photos, ...post.videos]) : current
        },
        variationMeta: {
          $set: getVariationMeta(post, method, context)
        },
        postFeedType: {
          $apply: current =>
            post.socialProfile.__typename === 'InstagramSocialProfile' && post.feedType ? post.feedType : current
        },
        publishMethod: {
          $apply: current =>
            post.socialProfile.__typename === 'InstagramSocialProfile' && post.publishMethod
              ? post.publishMethod
              : current
        },
        title: { $apply: current => post.title ?? current },
        varyMedia: { $set: getVariation(post.variationMeta) },
        texts: {
          $set: allTexts
        },
        firstComment: {
          $apply: (current: CustomParagraph[]) =>
            post.firstComment ? deserializeText(post.firstComment, [], transformLink(post.link || null)) : current
        },
        shouldShowFirstComment: { $apply: current => !!post.firstComment ?? current }
      },
      // postSource: { $apply: current => getPostSourceFromSubcontext(context, current) },
      postStatus: {
        $set:
          context === PostEditorContextEnum.reschedule_edit || context === PostEditorContextEnum.restore_trashed
            ? PostStatus.ACTIVE
            : post.status
      },
      postId: { $set: getPostId(context, post) },
      parentId: { $set: getParentId(context, post) }
      // postId: {
      //   $apply: current =>
      //     context === PostEditorContextEnum.reschedule_new || context === PostEditorContextEnum.duplicate
      //       ? null
      //       : current
      // }
      // postId: { $set: (type === 'reschedule' && post.status === PostStatus.FAILED) || !type ? post.id : null },
      // parentId: { $set: type === 'duplicate' ? null : post.parentId || null }
      // Notes for PostId and ParentId
      // If its 'duplicate', open the post editor as a new post with no parent -> no parent
      // If its a 'reschedule':
      //  - If post status is failed, open the post editor as editing the post -> add current parent Id
      // Otherwise, open the post editor as a new post and pass the post parent ID in so to keep the lineage
    });
  } catch (e) {
    showToast({
      severity: 'error',
      detail: 'Texts could not be serialized.',
      summary: 'Error mapping Post'
    });
    return state;
  }
};

const sortPosts: SortFunction<GetPostsEditorQuery['latestFamilyMembersOfPost'][number]> = (a, b) => {
  const pa = a.socialProfile;
  const pb = b.socialProfile;
  return profilesHelpers.sortProfiles(pa, pb);
};

export const mapFetchedPostsToState = (
  input: PostItemFragment[],
  postId: string,
  postGroup: PostGroupsType,
  bitlyAccounts: GetBitlyAccountsQuery,
  context: PostEditorContextEnum
): PostGroupsType => {
  const { update: updateImmutable } = ImmutabilityContext;
  const sortedAllPosts = cloneDeep(input).sort(sortPosts);
  let enabledStatuses: PostStatus[];
  switch (context) {
    case PostEditorContextEnum.reschedule_edit:
      enabledStatuses = [PostStatus.FAILED];
      break;
    case PostEditorContextEnum.restore_trashed:
      enabledStatuses = [PostStatus.TRASH];
      break;
    default:
      enabledStatuses = [PostStatus.ACTIVE, PostStatus.DRAFT];
  }
  const enabledPosts = sortedAllPosts.filter(p => enabledStatuses.includes(p.status));
  const disabledPosts =
    context === PostEditorContextEnum.edit
      ? sortedAllPosts.filter(p => [PostStatus.FAILED, PostStatus.PUBLISHED].includes(p.status))
      : [];
  const mainPost = enabledPosts.find(post => post.id === postId);
  const mainPostIndex = enabledPosts.findIndex(post => post.id === postId);
  // const mainDisabledPost = disabledPosts.find(post => post.id === postId);
  if (!mainPost) {
    throw new Error('The required post could not be mapped.');
  }
  const profiles: SelectedSocialProfileValue[] = enabledPosts.flatMap(post =>
    post.socialProfile
      ? {
          __typename: post.socialProfile.__typename,
          profileId: post.socialProfile.id,
          ...(post.pinterestBoardId ? { pnBoardId: post.pinterestBoardId } : {}),
          ...(post.tikTok ? { tikTok: mapPostTikTokToTikTokProfileValues(post.tikTok) } : {})
        }
      : []
  );
  const posts = enabledPosts.map(item => {
    return mapPostToGroupPostAttributesType(
      item,
      {
        ...postGroup.groupPostAttributes,
        customizedProfileId: item.socialProfile.id || postGroup.groupPostAttributes.customizedProfileId
      },
      context
    );
  });
  const readOnlyPosts = disabledPosts.map(item => ({
    ...mapPostToGroupPostAttributesType(
      item,
      {
        ...postGroup.groupPostAttributes,
        customizedProfileId: item.socialProfile.id || postGroup.groupPostAttributes.customizedProfileId
      },
      context
    ),
    active: true
  }));
  const profilesWithBitly = bitlyAccounts.workspace.socialProfiles;
  const defaultBitly = bitlyAccounts.workspace.settings.defaultBitly;
  return updateImmutable(postGroup, {
    categoryId: { $set: mainPost.category.id },
    familyUuid: { $set: context === PostEditorContextEnum.duplicate ? null : mainPost.familyUuid || null },
    profiles: {
      $set: profiles
    },
    customizeContentPerProfile: { $set: true }, // In edit mode we always keep customize on
    customizeSchedulePerProfile: { $set: true },
    bitlyAccount: {
      profilesWithBitly: { $set: profilesWithBitly },
      defaultBitly: { $set: defaultBitly }
    },
    activeCustomizeIndex: { $set: enabledPosts.length && mainPostIndex > -1 ? mainPostIndex : null },
    posts: {
      $apply: (current: GroupPostAttributesType[]) => (posts.length ? posts : current)
    },
    readOnlyPosts: {
      $apply: (current: ReadonlyGroupPostAttributesType[]) => (readOnlyPosts.length ? readOnlyPosts : current)
    }
  });
};

export const mapFetchedLinkToPostEditorLink = transformLink;
